import type { BalanceStatus } from '@features/invoices/api/types/balance-status.types';

import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltDatePicker } from '@cae/cobalt-react/components/date-picker';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useOrganizations } from '@contexts/organizations-provider';
import { CheckboxFilterSection } from '@shared/components/checkbox-filter-section';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { DraftFilterState, InvoicesFilterPanelProps } from './InvoicesFilterPanel.types';

import { useInvoicesFilter } from '../../contexts/filter/useInvoicesFilter';
import { useInvoiceFilters } from '../../hooks/use-invoice-filters/useInvoiceFilters';
import styles from './InvoicesFilterPanel.module.css';
import {
  BALANCE_STATUS_OPTIONS,
  EMPTY_DRAFT,
  dateRangeToValue,
  isDraftDirty,
  isDraftEmpty,
  toCheckboxOptions,
  toIsoDateString,
  toggleItem,
} from './InvoicesFilterPanel.utils';

const InvoicesFilterPanel = React.memo(
  ({ onClose }: Readonly<InvoicesFilterPanelProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const { selectedOrganization } = useOrganizations();
    const { filters, setFilters } = useInvoicesFilter();

    const {
      data: filterOptions,
      isLoading,
      isError,
    } = useInvoiceFilters(selectedOrganization?.id ?? '');

    const appliedDraft = React.useMemo<DraftFilterState>(
      () => ({
        dueDateStart: filters.dueDateStart,
        dueDateEnd: filters.dueDateEnd,
        issuedDateStart: filters.issuedDateStart,
        issuedDateEnd: filters.issuedDateEnd,
        balanceStatuses: filters.balanceStatuses,
        currencies: filters.currencies,
        platforms: filters.platforms,
        locations: filters.locations,
      }),
      [filters]
    );

    const [draft, setDraft] = React.useState<DraftFilterState>(appliedDraft);

    const handleDueDateSelect = React.useCallback(
      (date: Date | [Date, Date] | null): void => {
        if (Array.isArray(date)) {
          setDraft(prev => ({ ...prev, dueDateStart: toIsoDateString(date[0]), dueDateEnd: toIsoDateString(date[1]) }));
        } else {
          setDraft(prev => ({ ...prev, dueDateStart: null, dueDateEnd: null }));
        }
      },
      []
    );

    const handleIssuedDateSelect = React.useCallback(
      (date: Date | [Date, Date] | null): void => {
        if (Array.isArray(date)) {
          setDraft(prev => ({ ...prev, issuedDateStart: toIsoDateString(date[0]), issuedDateEnd: toIsoDateString(date[1]) }));
        } else {
          setDraft(prev => ({ ...prev, issuedDateStart: null, issuedDateEnd: null }));
        }
      },
      []
    );

    const handleBalanceStatusSelect = React.useCallback(
      (id: string): void => { setDraft(prev => ({ ...prev, balanceStatuses: toggleItem(prev.balanceStatuses, id as BalanceStatus) })); },
      []
    );
    const handleCurrencySelect = React.useCallback(
      (id: string): void => { setDraft(prev => ({ ...prev, currencies: toggleItem(prev.currencies, id) })); },
      []
    );
    const handlePlatformSelect = React.useCallback(
      (id: string): void => { setDraft(prev => ({ ...prev, platforms: toggleItem(prev.platforms, id) })); },
      []
    );
    const handleLocationSelect = React.useCallback(
      (id: string): void => { setDraft(prev => ({ ...prev, locations: toggleItem(prev.locations, id) })); },
      []
    );

    const handleApply = React.useCallback((): void => {
      setFilters(draft);
      onClose();
    }, [draft, setFilters, onClose]);

    const handleClear = React.useCallback((): void => {
      setFilters(EMPTY_DRAFT);
      onClose();
    }, [setFilters, onClose]);

    const applyDisabled = React.useMemo(() => !isDraftDirty(draft, appliedDraft), [draft, appliedDraft]);
    const clearDisabled = React.useMemo(() => isDraftEmpty(draft) && isDraftEmpty(appliedDraft), [draft, appliedDraft]);

    const currencyOptions = React.useMemo(() => toCheckboxOptions(filterOptions?.currencies ?? []), [filterOptions?.currencies]);
    const platformOptions = React.useMemo(() => toCheckboxOptions(filterOptions?.platforms ?? []), [filterOptions?.platforms]);
    const locationOptions = React.useMemo(() => toCheckboxOptions(filterOptions?.locations ?? []), [filterOptions?.locations]);

    const dueDateValue = React.useMemo(() => dateRangeToValue(draft.dueDateStart, draft.dueDateEnd), [draft.dueDateStart, draft.dueDateEnd]);
    const issuedDateValue = React.useMemo(() => dateRangeToValue(draft.issuedDateStart, draft.issuedDateEnd), [draft.issuedDateStart, draft.issuedDateEnd]);

    const hasFilterOptions = currencyOptions.length > 0 || platformOptions.length > 0 || locationOptions.length > 0;

    return (
      <CobaltSidePanel
        isOpen
        showCloseButton
        size={isMobileOrTablet ? 'large' : 'small'}
        title="Filters"
        onClose={onClose}
        actionBar={
          <CobaltActionBar
            position="right"
            variant="floating"
            tertiaryButton={
              <CobaltButton
                variant="ghost"
                onClick={handleClear}
                disabled={clearDisabled}
              >
                Clear
              </CobaltButton>
            }
            primaryButton={
              <CobaltButton
                variant="primary"
                onClick={handleApply}
                disabled={applyDisabled}
              >
                Apply
              </CobaltButton>
            }
          />
        }
      >
        <div
          className={styles.filterPanel}
          data-testid="invoices-filter-panel"
        >
          <div className={styles.dateRangeSection}>
            <span
              id="due-date-range-label"
              className={styles.dateRangeLabel}
            >
              Due date range
            </span>
            <CobaltDatePicker
              isRangePicker
              hasClear
              aria-labelledby="due-date-range-label"
              value={dueDateValue}
              onDateSelect={handleDueDateSelect}
              data-testid="due-date-range-picker"
            />
          </div>

          <div className={styles.dateRangeSection}>
            <span
              id="issued-date-range-label"
              className={styles.dateRangeLabel}
            >
              Issued date range
            </span>
            <CobaltDatePicker
              isRangePicker
              hasClear
              aria-labelledby="issued-date-range-label"
              value={issuedDateValue}
              onDateSelect={handleIssuedDateSelect}
              data-testid="issued-date-range-picker"
            />
          </div>

          <CheckboxFilterSection
            label="Outstanding balance"
            headingId="outstanding-balance-filter-heading"
            dataTestId="outstanding-balance-filter"
            options={BALANCE_STATUS_OPTIONS}
            selected={draft.balanceStatuses as string[]}
            onSelect={handleBalanceStatusSelect}
            hasDivider={false}
          />

          {isLoading && <ContentLoader />}
          {isError && <InlineFeedback type="data-error" />}

          {filterOptions && !hasFilterOptions && (
            <InlineFeedback type="so-empty" />
          )}

          {filterOptions && hasFilterOptions && (
            <>
              {currencyOptions.length > 0 && (
                <CheckboxFilterSection
                  label="Currency"
                  headingId="currency-filter-heading"
                  dataTestId="currency-filter"
                  options={currencyOptions}
                  selected={draft.currencies}
                  onSelect={handleCurrencySelect}
                  searchable
                />
              )}

              {platformOptions.length > 0 && (
                <CheckboxFilterSection
                  label="Platform"
                  headingId="platform-filter-heading"
                  dataTestId="platform-filter"
                  options={platformOptions}
                  selected={draft.platforms}
                  onSelect={handlePlatformSelect}
                  searchable
                />
              )}

              {locationOptions.length > 0 && (
                <CheckboxFilterSection
                  label="Location"
                  headingId="location-filter-heading"
                  dataTestId="location-filter"
                  options={locationOptions}
                  selected={draft.locations}
                  onSelect={handleLocationSelect}
                  searchable
                />
              )}
            </>
          )}
        </div>
      </CobaltSidePanel>
    );
  }
);

InvoicesFilterPanel.displayName = 'InvoicesFilterPanel';

export { InvoicesFilterPanel };
